// app/src/dashboard/widgets/useWidgetResize.ts
import { useRef, useCallback } from "react";
import { GRID_COLS } from "./types";

export type HandlePosition = "right" | "bottom" | "corner";

export interface ResizingState {
  slotId: string;
  colSpan: number;
  rowSpan: number;
  isBlocked: boolean;
}

interface SlotInfo {
  id: string;
  colStart: number;
  rowStart: number;
  colSpan: number;
  rowSpan: number;
}

interface RegistryConstraints {
  minColSpan: number;
  maxColSpan: number;
  minRowSpan: number;
  maxRowSpan: number;
}

interface UseWidgetResizeOptions {
  gridRef: React.RefObject<HTMLElement | null>;
  slot: SlotInfo;
  constraints: RegistryConstraints;
  allSlots: SlotInfo[];
  onResizing: (state: ResizingState | null) => void;
  onResizeEnd: (colSpan: number, rowSpan: number) => void;
}

/** Parse gridTemplateRows "150px 150px 150px" → array of px heights */
function parseGridRows(gridTemplateRows: string): number[] {
  return gridTemplateRows
    .trim()
    .split(/\s+/)
    .map((s) => parseFloat(s))
    .filter((n) => !isNaN(n));
}

/** Sum of gap values between columns from 1 to GRID_COLS */
function calcGapPx(gridEl: HTMLElement): number {
  const style = getComputedStyle(gridEl);
  // gap is "Xpx" or "Xpx Ypx" (row-gap col-gap)
  const parts = style.gap.trim().split(/\s+/);
  const colGap = parseFloat(parts[parts.length > 1 ? 1 : 0]) || 0;
  return colGap * (GRID_COLS - 1);
}

/** True if any other slot occupies cell (c, r) */
function isOccupied(
  c: number,
  r: number,
  currentSlotId: string,
  allSlots: SlotInfo[]
): boolean {
  for (const s of allSlots) {
    if (s.id === currentSlotId) continue;
    if (
      c >= s.colStart &&
      c < s.colStart + s.colSpan &&
      r >= s.rowStart &&
      r < s.rowStart + s.rowSpan
    ) {
      return true;
    }
  }
  return false;
}

/** Check whether the proposed rectangle collides with any other slot */
function hasCollision(
  colStart: number,
  rowStart: number,
  colSpan: number,
  rowSpan: number,
  slotId: string,
  allSlots: SlotInfo[]
): boolean {
  for (let c = colStart; c < colStart + colSpan; c++) {
    for (let r = rowStart; r < rowStart + rowSpan; r++) {
      if (isOccupied(c, r, slotId, allSlots)) return true;
    }
  }
  return false;
}

export function useWidgetResize({
  gridRef,
  slot,
  constraints,
  allSlots,
  onResizing,
  onResizeEnd,
}: UseWidgetResizeOptions) {
  const dragState = useRef<{
    handle: HandlePosition;
    startX: number;
    startY: number;
    baseColSpan: number;
    baseRowSpan: number;
    colWidth: number;
    rowHeights: number[];
    lastValidColSpan: number;
    lastValidRowSpan: number;
  } | null>(null);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragState.current) return;
      const {
        handle,
        startX,
        startY,
        baseColSpan,
        baseRowSpan,
        colWidth,
        rowHeights,
        lastValidColSpan,
        lastValidRowSpan,
      } = dragState.current;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const avgRowHeight =
        rowHeights.length > 0
          ? rowHeights.reduce((a, b) => a + b, 0) / rowHeights.length
          : 150;

      const maxColSpanAllowed = Math.min(
        constraints.maxColSpan,
        GRID_COLS - slot.colStart + 1
      );

      let newColSpan = baseColSpan;
      let newRowSpan = baseRowSpan;

      if (handle === "right" || handle === "corner") {
        newColSpan = Math.max(
          constraints.minColSpan,
          Math.min(
            maxColSpanAllowed,
            baseColSpan + Math.round(deltaX / colWidth)
          )
        );
      }
      if (handle === "bottom" || handle === "corner") {
        newRowSpan = Math.max(
          constraints.minRowSpan,
          Math.min(
            constraints.maxRowSpan,
            baseRowSpan + Math.round(deltaY / avgRowHeight)
          )
        );
      }

      const blocked = hasCollision(
        slot.colStart,
        slot.rowStart,
        newColSpan,
        newRowSpan,
        slot.id,
        allSlots
      );

      if (!blocked) {
        dragState.current.lastValidColSpan = newColSpan;
        dragState.current.lastValidRowSpan = newRowSpan;
      }

      onResizing({
        slotId: slot.id,
        colSpan: blocked ? lastValidColSpan : newColSpan,
        rowSpan: blocked ? lastValidRowSpan : newRowSpan,
        isBlocked: blocked,
      });
    },
    [slot, constraints, allSlots, onResizing]
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (!dragState.current) return;
      const { lastValidColSpan, lastValidRowSpan, baseColSpan, baseRowSpan } =
        dragState.current;
      const target = e.currentTarget as HTMLElement;
      target.removeEventListener("pointermove", handlePointerMove);
      target.removeEventListener("pointerup", handlePointerUp);
      target.releasePointerCapture(e.pointerId);
      dragState.current = null;

      const changed =
        lastValidColSpan !== baseColSpan || lastValidRowSpan !== baseRowSpan;
      // null clears the overlay in the parent
      onResizing(null);
      if (changed) {
        onResizeEnd(lastValidColSpan, lastValidRowSpan);
      }
    },
    [slot, handlePointerMove, onResizing, onResizeEnd]
  );

  const startResize = useCallback(
    (e: React.PointerEvent<HTMLElement>, handle: HandlePosition) => {
      if (!gridRef.current) return;
      e.stopPropagation();
      e.preventDefault();

      const gridEl = gridRef.current;
      const gridRect = gridEl.getBoundingClientRect();
      const gapPx = calcGapPx(gridEl);
      const colWidth = (gridRect.width - gapPx) / GRID_COLS;
      const rowHeights = parseGridRows(
        getComputedStyle(gridEl).gridTemplateRows
      );

      dragState.current = {
        handle,
        startX: e.clientX,
        startY: e.clientY,
        baseColSpan: slot.colSpan,
        baseRowSpan: slot.rowSpan,
        colWidth,
        rowHeights,
        lastValidColSpan: slot.colSpan,
        lastValidRowSpan: slot.rowSpan,
      };

      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      target.addEventListener("pointermove", handlePointerMove as EventListener);
      target.addEventListener("pointerup", handlePointerUp as EventListener);
    },
    [gridRef, slot, handlePointerMove, handlePointerUp]
  );

  return { startResize };
}
